import { useEffect } from 'react';

import type { Project } from '~hooks/useProjects/models';
import useProjects from '~hooks/useProjects/useProjects';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: the selected `project`'s id (watched, via the Provider). Keying on
 *           the project OBJECT loops — see the dep note in the effect body.
 * Writes:   the Provider's `projectData` state — the FULL Project entity
 *           behind the selected `project` form value. Downstream consumers:
 *           the OrderCustomerDetailsPanel embed (`project={projectData ?? undefined}`), the
 *           useVendorRateForDriverDerivation effect, the freight-rates
 *           auto-visibility check, and the project-change cascade that
 *           copies project defaults into the form fields.
 * Guards:   only fetches while a project with an id is selected; never
 *           clears on deselection (legacy keeps the last entity). Fetch
 *           failures are swallowed — useProjects().getProjectById already
 *           toasts + reports before rethrowing.
 * Legacy:   the legacy form's entity fetch (the value-cascade tail lives in
 *           useProjectCascade).
 * Test:     __tests__/OrderFormV3Context.test.tsx ("project entity fetch").
 */
export function useProjectDataFetch({
  watchedProject,
  setProjectData,
}: {
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  setProjectData: (project: Project | null) => void;
}): void {
  const { getProjectById } = useProjects();

  useEffect(() => {
    const fetchData = async () => {
      if (watchedProject?.id) {
        try {
          const projectData = await getProjectById(watchedProject.id);
          setProjectData(projectData);
        } catch {
          // Error already handled in the hook (toast + Sentry).
        }
      }
    };

    void fetchData();
    // Key on the project ID, NOT the watched project object (TRE-15128): the
    // Provider's useWatch('project') hands back a fresh object reference on
    // every render, so an object dep refetches on every render — an infinite
    // request loop on the edit form. The id is what actually selects the
    // entity; the fetcher is read via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedProject?.id]);
}
